/**
 * origen-visita.js — recuerda de dónde vino el visitante y lo adjunta a los
 * formularios, para poder atribuir cada lead a su campaña en Google Ads.
 *
 * Se carga en TODAS las páginas desde layout/theme.liquid, porque el clic del
 * anuncio casi nunca cae en la página de contacto: cae en una landing, el
 * usuario navega, y para cuando rellena el formulario los parámetros de la URL
 * ya se han perdido.
 *
 * CONSENTIMIENTO: la cookie solo se escribe si el visitante ha dado
 * consentimiento de MARKETING en el banner. No es una cookie técnica —su
 * finalidad es atribución publicitaria—, así que sin consentimiento no se
 * guarda nada y el formulario sale sin línea "Origen". Si el visitante
 * rechaza explícitamente, la cookie se borra; si simplemente no ha decidido,
 * no se toca (borrar es destructivo y la cookie solo ha podido escribirse con
 * un consentimiento previo).
 *
 * Esa última premisa la sostiene el sello `v` de la cookie: las escritas
 * antes de condicionarla al consentimiento no lo llevan y se purgan al primer
 * acceso, sin mirar el estado del consentimiento.
 *
 * El momento de capturar importa: el banner aparece en la página de
 * aterrizaje, que es justo donde todavía están el `gclid` y el referrer. Por
 * eso se escucha `visitorConsentCollected` además de leer el estado al
 * arrancar — quien acepta ahí conserva su atribución. Quien acepta tres
 * páginas después ya ha perdido los parámetros, y eso es inevitable: no se
 * pueden guardar "por si acaso" antes de tener permiso.
 *
 * Qué guarda: gclid, utm_source, utm_medium, utm_campaign, utm_term y el
 * referrer externo de la visita que los trajo.
 *
 * Regla de prioridad (un clic de pago no se pierde por una visita posterior):
 *   · La visita trae gclid        → manda siempre (el clic de Ads más reciente).
 *   · La visita trae utm_* sin gclid → solo manda si lo guardado no tenía gclid.
 *   · La visita no trae nada      → se respeta lo guardado (directa u orgánica
 *                                   posterior NUNCA pisan un origen de Ads).
 *
 * Expone `window.SurisaOrigen.resumen()` y rellena cualquier
 * `<input data-origen-field>` con un texto legible para el email, del tipo
 * "Google Ads | campaña: rebajas | búsqueda: din 2093".
 *
 * Ver docs/ARCHITECTURE_analytics.md.
 */
(function () {
  'use strict';

  var COOKIE = 'surisa_origen';
  var DIAS = 30;
  var MARCAS = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term'];

  /* Sello de formato. La v1 (sin sello) se escribía ANTES de condicionar la
     cookie al consentimiento, así que no se puede dar por consentida: se
     purga al primer acceso. Subir este número también sirve para cualquier
     cambio futuro del contenido de la cookie. */
  var VERSION = 2;

  function leerCookie() {
    try {
      var trozo = ('; ' + document.cookie).split('; ' + COOKIE + '=')[1];
      if (!trozo) return null;
      return JSON.parse(decodeURIComponent(trozo.split(';')[0]));
    } catch (e) {
      return null; // Cookie corrupta o de una versión anterior: se reescribe.
    }
  }

  function escribirCookie(datos) {
    try {
      datos.v = VERSION;
      var valor = encodeURIComponent(JSON.stringify(datos));
      var seguro = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = COOKIE + '=' + valor + '; Max-Age=' + (DIAS * 86400) +
        '; Path=/; SameSite=Lax' + seguro;
    } catch (e) {
      /* Sin cookies, el origen vale solo para esta página. */
    }
  }

  function borrarCookie() {
    try {
      document.cookie = COOKIE + '=; Max-Age=0; Path=/';
    } catch (e) {
      /* Nada que borrar. */
    }
  }

  function marcasDeLaUrl() {
    var datos = {};
    try {
      var params = new URLSearchParams(window.location.search);
      for (var i = 0; i < MARCAS.length; i++) {
        var v = params.get(MARCAS[i]);
        if (v) datos[MARCAS[i]] = v.slice(0, 200); // Tope defensivo.
      }
    } catch (e) {
      /* Navegador sin URLSearchParams: se queda sin marcas. */
    }
    return datos;
  }

  // El referrer interno no es un origen: navegar por la web no cambia de dónde
  // vino el visitante.
  function referrerExterno() {
    var ref = document.referrer || '';
    if (!ref) return '';
    try {
      if (new URL(ref).hostname === window.location.hostname) return '';
    } catch (e) {
      return '';
    }
    return ref;
  }

  function capturar() {
    var guardado = leerCookie();
    var nuevas = marcasDeLaUrl();
    var tieneMarcas = Object.keys(nuevas).length > 0;

    if (!tieneMarcas) {
      if (guardado) return guardado; // Nada nuevo: se respeta lo que hubiera.
      var inicial = { referrer: referrerExterno() };
      escribirCookie(inicial);
      return inicial;
    }
    if (guardado && guardado.gclid && !nuevas.gclid) return guardado;

    nuevas.referrer = referrerExterno();
    escribirCookie(nuevas);
    return nuevas;
  }

  function dominio(url) {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch (e) {
      return '';
    }
  }

  function resumen() {
    var o = leerCookie();
    if (!o) return ''; // Sin consentimiento no hay origen que contar.

    var base;
    if (o.gclid) {
      base = 'Google Ads';
    } else {
      var host = dominio(o.referrer || '');
      if (!host) base = 'Directo';
      else if (/(^|\.)google\./.test(host)) base = 'Google orgánico';
      else if (/(^|\.)bing\./.test(host)) base = 'Bing orgánico';
      else base = 'Otro: ' + host;
    }

    if (o.utm_campaign) base += ' | campaña: ' + o.utm_campaign;
    if (o.utm_term) base += ' | búsqueda: ' + o.utm_term;
    return base;
  }

  function rellenarCampos() {
    var texto = resumen();
    var campos = document.querySelectorAll('[data-origen-field]');
    for (var i = 0; i < campos.length; i++) {
      if (texto) {
        // El `name` se pone aquí y no en el Liquid: un formulario sin JS, o sin
        // consentimiento, no manda una línea "Origen:" vacía al email.
        campos[i].setAttribute('name', 'contact[Origen]');
        campos[i].value = texto;
      } else {
        campos[i].removeAttribute('name');
        campos[i].value = '';
      }
    }
  }

  /**
   * estado: 'yes' (acepta) · 'no' (rechaza) · '' (todavía no ha decidido).
   *
   * Sin decidir NO se borra nada, a propósito: borrar es destructivo y la
   * cookie solo ha podido escribirse con un consentimiento previo. Si el
   * estado llegara vacío por un fallo transitorio de la API, borrar aquí
   * destruiría la atribución de alguien que sí había aceptado.
   */
  function aplicar(estado) {
    if (estado === 'yes') capturar();
    else if (estado === 'no') borrarCookie();
    rellenarCampos();
  }

  // Aceptar (o rechazar) en el banner, sin recargar la página.
  document.addEventListener('visitorConsentCollected', function (evento) {
    var d = (evento && evento.detail) || {};
    aplicar(d.marketingAllowed ? 'yes' : 'no');
  });

  function leerEstado() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.currentVisitorConsent !== 'function') return;
    var consentimiento = api.currentVisitorConsent() || {};
    aplicar(consentimiento.marketing || '');
  }

  function cargarApi() {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return false;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.2' }],
      function (error) { if (!error) leerEstado(); }
    );
    return true;
  }

  /* Purga de la v1. Se hace SIEMPRE y antes que nada, sin mirar el estado del
     consentimiento: esas cookies se escribieron sin pedirlo, así que borrarlas
     es correcto en todos los casos. Es lo que permite que la regla de «no
     borrar si no ha decidido» siga siendo segura — a partir de aquí, toda
     cookie que exista lleva sello y se escribió con consentimiento. */
  function purgarLegado() {
    var guardado = leerCookie();
    if (guardado && guardado.v !== VERSION) borrarCookie();
  }

  purgarLegado();

  window.SurisaOrigen = { resumen: resumen };

  // Shopify.loadFeatures lo define content_for_header; este script va con
  // `defer`, pero se reintenta por si llega antes (máx ~2 s).
  if (!cargarApi()) {
    var intentos = 0;
    var temporizador = setInterval(function () {
      if (cargarApi() || ++intentos > 40) clearInterval(temporizador);
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rellenarCampos);
  } else {
    rellenarCampos();
  }
})();
